// Plans the brushstrokes that paint a painting in: read from its own pixels (forms, direction, detail), background first, focal subjects last.
const A = 96;

function analyse(img) {
  const W = A;
  const H = Math.max(8, Math.round((A * img.naturalHeight) / img.naturalWidth));
  const L = new Float32Array(W * H);
  try {
    const c = document.createElement("canvas");
    c.width = W;
    c.height = H;
    const g = c.getContext("2d", { willReadFrequently: true });
    g.drawImage(img, 0, 0, W, H);
    const d = g.getImageData(0, 0, W, H).data;
    for (let i = 0; i < W * H; i++) L[i] = (0.3 * d[i * 4] + 0.59 * d[i * 4 + 1] + 0.11 * d[i * 4 + 2]) / 255;
  } catch {
    // an unreadable image still gets painted in, just without reading its forms
  }
  const gx = new Float32Array(W * H);
  const gy = new Float32Array(W * H);
  for (let y = 1; y < H - 1; y++)
    for (let x = 1; x < W - 1; x++) {
      const i = y * W + x;
      gx[i] = L[i + 1] - L[i - 1];
      gy[i] = L[i + W] - L[i - W];
    }
  // local structure: the direction the forms run, and how busy the area is
  const tensor = (x, y) => {
    let xx = 0, yy = 0, xy = 0;
    for (let j = -3; j <= 3; j++)
      for (let k = -3; k <= 3; k++) {
        const px = Math.min(W - 2, Math.max(1, Math.round(x) + k));
        const py = Math.min(H - 2, Math.max(1, Math.round(y) + j));
        const i = py * W + px;
        xx += gx[i] * gx[i];
        yy += gy[i] * gy[i];
        xy += gx[i] * gy[i];
      }
    const along = 0.5 * Math.atan2(2 * xy, xx - yy) + Math.PI / 2;
    const coh = Math.sqrt((xx - yy) ** 2 + 4 * xy * xy) / (xx + yy + 1e-6);
    return { along, coh, mag: Math.sqrt(xx + yy) };
  };
  return { W, H, tensor };
}

const rnd = (s) => {
  const x = Math.sin(s * 127.1 + 311.7) * 43758.5453;
  return x - Math.floor(x);
};

function angleFn(style, an, cx, cy) {
  return (x, y) => {
    if (style === "vertical") return Math.PI / 2 + 0.18 * Math.sin(x * 0.7);
    if (style === "horizontal") return 0.14 * Math.sin(y * 0.5 + x * 0.05);
    if (style === "circular") return Math.atan2(y - cy, x - cx) + Math.PI / 2;
    if (style === "radial") return Math.atan2(y - cy, x - cx);
    const t = an.tensor(x, y);
    return t.coh > 0.25 ? t.along : Math.sin(x * 0.11) * 1.4 + Math.cos(y * 0.09);
  };
}

// a stroke follows the direction field a little way both sides of its centre
function trace(x, y, len, ang) {
  const n = 8;
  const step = len / n;
  const pts = [[x, y]];
  for (const sgn of [1, -1]) {
    let px = x, py = y, dx = 0, dy = 0;
    for (let i = 0; i < n / 2; i++) {
      const a = ang(px, py);
      let ux = Math.cos(a), uy = Math.sin(a);
      if (i === 0 && sgn < 0) { ux = -Math.cos(ang(x, y)); uy = -Math.sin(ang(x, y)); }
      if (i > 0 && ux * dx + uy * dy < 0) { ux = -ux; uy = -uy; }
      dx = ux;
      dy = uy;
      px += ux * step;
      py += uy * step;
      sgn > 0 ? pts.push([px, py]) : pts.unshift([px, py]);
    }
  }
  return pts;
}

function originTerm(from, x, y, W, H) {
  const u = x / W, v = y / H;
  if (!from) return 0;
  if (from === "top") return v;
  if (from === "bottom") return 1 - v;
  if (from === "left") return u;
  if (from === "right") return 1 - u;
  const [fx, fy] = from === "center" ? [0.5, 0.5] : from;
  return Math.min(1, Math.hypot(u - fx, v - fy) / 0.75);
}

const inRect = (r, u, v) => u >= r[0] && u <= r[0] + r[2] && v >= r[1] && v <= r[1] + r[3];

// a lead stroke that carries the journey's climax into the painting: smoke curling up, paint pouring, a swirl
function leadStroke(lead, W, H, cell) {
  const [fx, fy] = lead.at || [0.5, 0.5];
  const pts = [];
  for (let i = 0; i <= 24; i++) {
    const s = i / 24;
    if (lead.type === "curl") pts.push([(fx + 0.06 * s * Math.sin(s * 9)) * W, (fy - 0.55 * s) * H]);
    else if (lead.type === "pour") pts.push([(fx + 0.05 * Math.sin(s * 7)) * W, (fy + 0.6 * s) * H]);
    else pts.push([(fx + 0.3 * s * Math.cos(s * 7)) * W, (fy + 0.3 * s * Math.sin(s * 7)) * H]);
  }
  return { pts, w: cell * 1.3, t0: 0, dur: 1.6, seed: 7 };
}

export function planStrokes(img, cfg = {}) {
  const an = analyse(img);
  const { W, H } = an;
  const D = cfg.duration ?? 10;
  const cell = Math.max(W, H) / 14;
  const [cx, cy] = (cfg.center || [0.5, 0.5]).map((c, i) => c * (i ? H : W));
  const ang = angleFn(cfg.style || "flow", an, cx, cy);
  const focus = cfg.focus || [];
  const inFocus = (x, y) => focus.findIndex((f) => inRect(f.rect, x / W, y / H));
  const groups = [[], [], ...focus.map(() => [])];
  let seed = 1;
  // broad strokes of the ground, then finer dabs where the painting is busy
  for (const [size, layer] of [[cell, 0], [cell / 2, 1]])
    for (let y = size / 2; y < H; y += size)
      for (let x = size / 2; x < W; x += size) {
        const px = x + (rnd(seed++) - 0.5) * size * 0.7;
        const py = y + (rnd(seed++) - 0.5) * size * 0.7;
        if (inFocus(px, py) >= 0) continue;
        const mag = an.tensor(px, py).mag;
        if (layer === 1 && mag < 0.35) continue;
        const score = (layer ? 0 : Math.min(1, mag * 1.6)) * 0.5 + originTerm(cfg.from, px, py, W, H) * 0.45 + rnd(seed++) * 0.12;
        groups[layer].push({ pts: trace(px, py, size * (layer ? 1.8 : 2.4), ang), w: size * (layer ? 0.75 : 1.05), score, seed: seed++ });
      }
  // each focal subject in turn, its strokes following its own form
  focus.forEach((f, k) => {
    const [rx, ry, rw, rh] = f.rect;
    const fc = [(rx + rw / 2) * W, (ry + rh / 2) * H];
    const fang = angleFn(f.style || cfg.style || "flow", an, fc[0], fc[1]);
    const size = cell / 2.2;
    for (let y = ry * H; y < (ry + rh) * H; y += size * 0.8)
      for (let x = rx * W; x < (rx + rw) * W; x += size * 0.8) {
        const px = x + (rnd(seed++) - 0.3) * size * 0.6;
        const py = y + (rnd(seed++) - 0.3) * size * 0.6;
        groups[2 + k].push({ pts: trace(px, py, size * 1.9, fang), w: size * 0.9, score: rnd(seed++), seed: seed++ });
      }
  });
  const lead = cfg.lead ? leadStroke(cfg.lead, W, H, cell) : null;
  const t0 = lead ? 1.0 : 0;
  const span = D - t0 - 0.9;
  const nf = focus.length;
  const windows = [[0, nf ? 0.55 : 0.68], [0.4, nf ? 0.72 : 0.97], ...focus.map((_, k) => [0.62 + (0.35 * k) / nf, 0.62 + (0.35 * (k + 1)) / nf])];
  const out = lead ? [lead] : [];
  groups.forEach((g, gi) => {
    g.sort((a, b) => a.score - b.score);
    const [a, b] = windows[gi];
    g.forEach((s, i) => out.push({ ...s, t0: t0 + span * (a + ((b - a) * i) / Math.max(1, g.length)), dur: 0.55 + rnd(s.seed) * 0.35 }));
  });
  return { strokes: out, W, H, D };
}
